/** Follow rendered ancestry through slots and shadow hosts. */
export function composedParentElement(element: Element): Element | null {
  if (element.assignedSlot) return element.assignedSlot;
  if (element.parentElement) return element.parentElement;
  const root = element.getRootNode();
  return root instanceof ShadowRoot ? root.host : null;
}

/** The page remains the fallback when no inner element owns vertical scrolling. */
export function findVerticalScrollContainer(
  element: Element,
  includeFitting = false,
): HTMLElement | null {
  let fitting: HTMLElement | null = null;
  for (
    let parent = composedParentElement(element);
    parent;
    parent = composedParentElement(parent)
  ) {
    if (
      parent instanceof HTMLElement &&
      parent !== document.scrollingElement &&
      parent !== document.documentElement &&
      parent !== document.body &&
      /^(auto|scroll|overlay)$/.test(getComputedStyle(parent).overflowY)
    ) {
      if (parent.scrollHeight > parent.clientHeight) return parent;
      if (includeFitting && !fitting) fitting = parent;
    }
  }
  return fitting;
}

/** Keep scroll adjustments inside the host, even after its content shrinks. */
export function scrollRootBy(element: Element, delta: number): void {
  const owner = findVerticalScrollContainer(element, true);
  if (!owner) {
    window.scrollBy(0, delta);
    return;
  }
  const scale = owner.offsetHeight
    ? owner.getBoundingClientRect().height / owner.offsetHeight || 1
    : 1;
  owner.scrollTop += delta / scale;
}

/** Reveal the embed start without scrolling every ancestor/background page. */
export function scrollToStart(element: Element): void {
  const owner = findVerticalScrollContainer(element, true);
  const bounds = owner?.getBoundingClientRect();
  const scale = owner?.offsetHeight
    ? (bounds?.height ?? 0) / owner.offsetHeight || 1
    : 1;
  const top = owner && bounds
    ? bounds.top + owner.clientTop * scale
    : window.visualViewport?.offsetTop ?? 0;
  scrollRootBy(element, element.getBoundingClientRect().top - top);
}
